import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ModalController, ToastController, IonPopover } from '@ionic/angular';
import { WebhookService } from 'src/app/providers/webhook-service/webhook-service';
import { Webhook } from 'src/app/models/Webhook';
import { TranslationBridge } from 'src/app/providers/translation-bridge/translation-bridge';

interface WebhookExecution {
  id?: string;
  transactionId: string;
  accountId: string;
  date: Date;
  code: string;
  response: string;
  status?: string;
  webhookId?: string;
}

interface GroupedExecutions {
  date: string;
  displayDate: string;
  executions: WebhookExecution[];
}

@Component({
  selector: 'webhook-executions-modal',
  templateUrl: './webhook-executions-modal.component.html',
  styleUrls: ['./webhook-executions-modal.component.scss'],
})
export class WebhookExecutionsModalComponent implements OnInit {

  @ViewChild('actionPopover') actionPopover: IonPopover;
  
  @Input() webhook: Webhook;

  executions: WebhookExecution[] = [];
  groupedExecutions: GroupedExecutions[] = [];
  loading: boolean = false;
  selectedStatus: string = 'all';
  
  isActionPopoverOpen: boolean = false;
  selectedExecution: WebhookExecution = null;

  constructor(
    private modalCtrl: ModalController,
    private webhookService: WebhookService,
    private translate: TranslationBridge,
    private toastController: ToastController
  ) { }

  ngOnInit() {
    if (this.webhook) {
      this.loadExecutions();
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  async loadExecutions() {
    if (!this.webhook?.id) return;
    
    this.loading = true;
    try {
      const status = this.selectedStatus === 'all' ? null : this.selectedStatus;
      this.executions = await this.webhookService.getWebhookExecutionsByWebhookId(
        this.webhook.id, 
        status
      );
      this.groupExecutionsByDate();
    } catch (error) {
      console.error('Failed to load webhook executions:', error);
      this.executions = [];
      this.groupedExecutions = [];
      this.toastController.create({
        message: 'Failed to load webhook executions. Please try again.',
        duration: 3000,
        position: 'top',
        cssClass: 'toast-error'
      }).then((toast) => {
        toast.present();
      });
    } finally {
      this.loading = false;
    }
  }

  onStatusChange(event: any) {
    this.selectedStatus = event.detail.value;
    this.loadExecutions();
  }

  groupExecutionsByDate() {
    const groups = new Map<string, WebhookExecution[]>();
    
    // Sort executions by date (newest first)
    const sortedExecutions = [...this.executions].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
    
    // Group by date
    sortedExecutions.forEach(execution => {
      const date = new Date(execution.date);
      const dateKey = date.toDateString(); // e.g., "Mon Dec 25 2023"
      
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey)!.push(execution);
    });
    
    // Convert to array with display dates
    this.groupedExecutions = Array.from(groups.entries()).map(([dateKey, executions]) => {
      const date = new Date(dateKey);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      
      let displayDate: string;
      if (date.toDateString() === today.toDateString()) {
        displayDate = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        displayDate = 'Yesterday';
      } else {
        displayDate = date.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
      
      return {
        date: dateKey,
        displayDate,
        executions: executions.sort((a, b) => {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        })
      };
    });
  }

  onExecutionAction(event: MouseEvent, execution: WebhookExecution) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedExecution = execution;
    this.isActionPopoverOpen = true;
    this.actionPopover.event = event;
  }

  async resendWebhookExecution() {
    this.isActionPopoverOpen = false;
    if (!this.selectedExecution || !this.webhook) return;

    try {
      const transactionIds = [this.selectedExecution.transactionId];
      await this.webhookService.sendTransactionsToWebhook(
        this.webhook.id, 
        transactionIds
      );
      
      this.toastController.create({
        message: `Webhook execution resent successfully`,
        duration: 3000,
        position: 'top',
        color: 'success'
      }).then((toast) => {
        toast.present();
      });
      
      // Reload executions to show the new attempt
      setTimeout(() => {
        this.loadExecutions();
      }, 1000);
      
    } catch (error) {
      console.error('Failed to resend webhook:', error);
      this.toastController.create({
        message: 'Failed to resend webhook. Please try again.',
        duration: 3000,
        position: 'top',
        cssClass: 'toast-error'
      }).then((toast) => {
        toast.present();
      });
    }
  }

  copyExecutionDetails() {
    this.isActionPopoverOpen = false;
    if (!this.selectedExecution) return;
    
    const details = `Transaction ID: ${this.selectedExecution.transactionId}
Account ID: ${this.selectedExecution.accountId}
Date: ${this.formatDate(this.selectedExecution.date)}
Status Code: ${this.selectedExecution.code}
Response: ${this.selectedExecution.response || 'No response'}`;
    
    navigator.clipboard.writeText(details).then(() => {
      this.toastController.create({
        message: 'Execution details copied to clipboard',
        duration: 2000,
        position: 'top'
      }).then((toast) => {
        toast.present();
      });
    }).catch(() => {
      this.toastController.create({
        message: 'Failed to copy to clipboard',
        duration: 2000,
        position: 'top',
        cssClass: 'toast-error'
      }).then((toast) => {
        toast.present();
      });
    });
  }

  getStatusColor(code: string): string {
    const numCode = parseInt(code);
    if (numCode >= 200 && numCode < 300) return 'success';
    if (numCode >= 400 && numCode < 500) return 'warning';
    if (numCode >= 500) return 'danger';
    return 'medium';
  }

  getStatusIcon(code: string): string {
    const numCode = parseInt(code);
    if (numCode >= 200 && numCode < 300) return 'check-circle-duotone.svg';
    if (numCode >= 400) return 'x-circle-duotone.svg';
    return 'clock-duotone.svg';
  }

  formatDate(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleString();
  }

  formatTime(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  }

  getResponsePreview(response: string): string {
    if (!response) return 'No response';
    if (response.length <= 100) return response;
    return response.substring(0, 100) + '...';
  }

  getExecutionSummary(): string {
    const total = this.executions.length;
    const successful = this.executions.filter(e => {
      const code = parseInt(e.code);
      return code >= 200 && code < 300;
    }).length;
    const failed = total - successful;
    
    return `${total} total, ${successful} successful, ${failed} failed`;
  }
}